<template>
  <!-- eslint-disable -->
  <PinturaEditor
    ref="pinturaEditor"
    v-bind="editorDefaults"
    :src="src"
    :util="util"
    :imageAnnotation.sync="positionAreaAnnotation"
    :imageDecoration="activePositionShapes"
    :preview-upscale="true"
    :markup-editor-zoom-levels="[
      0.1, 0.15, 0.2, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3,
    ]"
    :markup-editor-zoom-adjust-step="0.01"
    :markup-editor-zoom-adjust-factor="0.1"
    :beforeUpdateShape="beforeUpdateShape"
    :beforeSelectShape="beforeSelectShape"
    :beforeDeselectShape="beforeDeselectShape"
    :beforeRemoveShape="beforeRemoveShape"
    @pintura:updateshape="updateShape"
    @select-shape="selectShape"
  />
</template>
<script>
import Vue, { PropType } from 'vue';

import {
  isPercentageValue,
  limitToContextWithCorrectionFactor,
  mm2px,
  px2mm,
  recalculateDPI,
} from '@/utils/common';

import {
  /**
   * Import the default image reader and writer
   */
  createDefaultImageReader,
  createDefaultImageWriter,

  /**
   * The method used to register the plugins
   */
  setPlugins,

  /**
   * The plugins we want to use
   */
  plugin_decorate,
  plugin_crop,
  plugin_annotate,

  /**
   * The user interface and plugin locale objects
   */
  locale_en_gb,
  plugin_decorate_locale_en_gb,

  /**
   * Because we use the annotate plugin we also need to import the markup editor
   * locale
   */
  markup_editor_locale_en_gb,

  /**
   * Import the default configuration for the markup editor and finetune
   * plugins markup_editor_defaults,
   */
  createNode,
  findNode,
  appendNode,
  colorStringToColorArray,
} from 'pintura';

import { PinturaEditor } from '@/components/w2pPintura/PinturaEditor';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { mapState, mapActions, mapStores } from 'pinia';
import { lineWidth } from '@/constants/areaLineWidth';
import { getShapePositionInAreainPt } from '@/utils/areaHelper';

setPlugins(plugin_decorate, plugin_crop, plugin_annotate);

export default Vue.extend({
  name: 'W2pPintura',
  components: {
    PinturaEditor,
  },
  data() {
    return {
      src: require('../../../../assets/resizedCheckerTransparency.png'),
      activeInteractionMode: 'auto',
      util: 'finetune',
      // Our editor custom configuration options
      editorDefaults: {
        imageReader: createDefaultImageReader(),
        imageWriter: createDefaultImageWriter(),
        stickerEnableSelectImage: false,
        // NOTE: To enable the shapes from the annotation array to be selectable try setting this to something different
        annotateActiveTool: 'rectangle',
        // NOTE: To enable the shapes from the annotation array to be selectable try setting this to something different
        utils: ['decorate'],
        decorateTools: [],
        decorateActiveTool: 'move',
        enableToolbar: true,
        enableButtonExport: false,
        enableButtonRevert: false,
        enableNavigateHistory: false,

        // The icons and labels to use in the user interface (required)
        locale: {
          ...locale_en_gb,
          ...plugin_decorate_locale_en_gb,
          ...markup_editor_locale_en_gb,
        },
        willRenderShapeControls: (controls, _selectedShapeId) => {
          const alphaNode = findNode('beta', controls);
          const shape = this.shapes.find(
            (shape) => shape.id === _selectedShapeId
          );

          const isTextShape = shape?.w2pText !== undefined;

          if (isTextShape) {
            alphaNode[3].forEach((tool, index) => {
              alphaNode[3][index] = false;
            });
          }
        },
      },
    };
  },

  computed: {
    ...mapState(useProductStore, ['activePosition']),
    ...mapState(useShapeStore, ['positionShapes', 'shapes']),

    positionAreaAnnotation() {
      if (this.activePosition.printArea) {
        if (this.activePosition.printArea.shapeType === 'Rectangle') {
          const { x, y, width, height, rotation } = this.printPositionArea(
            this.activePosition.id,
            true
          );

          return [
            {
              id: this.activePosition.id,
              strokeColor: colorStringToColorArray('rgba(36, 161, 72, 1)'),
              strokeWidth: `${lineWidth}%`,
              rotation,
              x,
              y,
              width,
              height,
            },
          ];
        }

        if (this.activePosition.printArea.shapeType === 'Ellipse') {
          const { x, y, rx, ry, rotation } = this.printPositionArea(
            this.activePosition.id,
            true
          );

          return [
            {
              id: this.activePosition.id,
              strokeColor: colorStringToColorArray('rgba(36, 161, 72, 1)'),
              strokeWidth: `${lineWidth}%`,
              rotation,
              x,
              y,
              rx,
              ry,
            },
          ];
        }

        if (this.activePosition.printArea.shapeType === 'Polygon') {
          const { points } = this.printPositionArea(
            this.activePosition.id,
            true
          );

          return [
            {
              id: this.activePosition.id,
              strokeColor: colorStringToColorArray('rgba(36, 161, 72, 1)'),
              strokeWidth: `${lineWidth}%`,
              rotation: 0,
              points,
            },
          ];
        }
      }

      return [];
    },

    activePositionShapes() {
      console.log('activePositionShapes', this.positionShapes);
      return this.positionShapes(this.activePosition.id);
    },
  },

  methods: {
    ...mapActions(useProductStore, ['printPositionArea']),
    ...mapActions(useShapeStore, ['updateShapePosition']),

    updateShape(current) {
      /**
       * UPDATE shape x, y, rotation
       */

      const scaledArea = this.printPositionArea(this.activePosition.id, true);
      const productArea = this.activePosition.printArea;

      this.updateShapePosition(current, scaledArea, productArea);
    },

    selectShape(id) {
      this.shapes[0].isSelected = true;
    },

    beforeSelectShape(current, target) {
      this.$emit('beforeselect-shape', target);
      return true;
    },

    beforeUpdateShape(shape, props, ctx) {
      if (props.width !== undefined) {
        // min 1px
        // prevent logo from disappearing
        // keep aspect ratio
        props.width = props.width < 1 ? 1 : props.width;
        props.height = props.width === 1 ? shape.height : props.height;
      }

      if (props.height !== undefined) {
        // min 1px
        // prevent logo from disappearing
        // keep aspect ratio
        props.height = props.height < 1 ? 1 : props.height;
        props.width = props.height === 1 ? shape.width : props.width;
      }

      const limit = limitToContextWithCorrectionFactor(
        ctx,
        undefined, // use default offset
        props,
        shape
      );

      /**app
       * *** SPECIAL: In case we get a rotation property inside props -
       * Here x and y aren't set at all, so we need to return the orginal back
       * without any modification
       *
       * VERY IMPORTANT:
       * Bear in mind props will be of a different shape, depending on action
       * being performed
       */
      const rotation = ((shape.rotation * 180) / Math.PI) % 360;
      if ((rotation !== 0 && rotation !== 180) || props.rotation) {
        return props;
      }

      this.$emit('beforeupdate-shape', shape);

      /**
       * Otherwise apply limitation to x and y
       * cases like scaling and moving operations
       */
      return {
        ...props,
        x: limit.x,
        y: limit.y,
      };
    },

    beforeDeselectShape(current, target) {
      this.$emit('beforedeselect-shape', { current, target });
      return true;
    },

    beforeRemoveShape(shape) {
      return true;
    },
  },
});
</script>

<style lang="scss">
@import '../../../../styles/_carbon-utils';
@import '../../../../styles/carbon-overrides.scss';
@import '../../../../../node_modules/pintura/pintura.css';

@import url('https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,300;0,400;0,700;1,400&display=swap');
#root {
  position: relative;
  height: 100%;

  #header {
    position: relative;
  }
}

.pintura-editor {
  --color-background: 244, 244, 244;
  --font-family: 'Roboto', sans-serif;
  --font-size: 16px;
}

.PinturaRoot {
  padding-top: 0;
  background: #e0e0e0 !important ;
}
.PinturaRoot[data-env~='landscape'] > .PinturaNavTools {
  justify-content: left;
}
.CustomTabList {
  display: none;
  background: #f4f4f4;

  .PinturaButton {
    width: 3rem;
    height: 3rem;
    border: none;
    padding: 0.1rem;
    margin: 0;
  }
}
.PinturaRoot[data-env~='landscape'] > .PinturaNavMain {
  display: none;
}
.PinturaRoot .PinturaEditorOverlay {
  background-color: rgba(0, 0, 0, 0.6);
}
.custom-shape-control.PinturaButtonIconOnly {
  width: 2.3em;
  height: 2.3em;
  svg {
    fill: white;
  }
}
.custom-bring-to-front.PinturaButtonIconOnly {
  svg {
    width: 80%;
  }
}
</style>

<style lang="scss">
.pintura-sticker-editor {
  --editor-modal-overlay-opacity: 0.65;
  --editor-modal-border-radius: 1.5rem;
  --editor-max-width: 48%;
  --editor-max-height: 50%;
}
</style>
